import { XMarkIcon } from '@heroicons/react/24/outline'
import {
  fieldOverrideDiffersFromSnapshot,
  formatLedgerFieldValue,
} from '@/components/ledgerOverrideStatus'
import type { WarehouseLedgerTransactionItem } from '@/services/warehouseTypes'

const VALUE_COLUMN_CLASS =
  'w-[300px] min-w-[300px] max-w-[300px] align-top !whitespace-normal'

const VALUE_CELL_CONTENT_CLASS = 'max-w-[300px] break-words whitespace-normal'

const FIELD_COLUMN_CLASS = 'table-sticky-pinned table-sticky-cell table-locked-edge align-top'

const FIELD_HEADER_STYLE = {
  position: 'sticky' as const,
  left: 0,
  zIndex: 10,
}

const FIELD_CELL_STYLE = {
  position: 'sticky' as const,
  left: 0,
  zIndex: 5,
}

const OVERRIDE_FIELD_ORDER = [
  'txn_date',
  'bk_sku',
  'product_name',
  'order_number',
  'kind',
  'store',
  'signed_cases',
  'unit_cost',
  'linked_return_of_bk_txn_id',
  'notes',
  'username',
] as const

const OVERRIDE_FIELD_LABELS: Record<string, string> = {
  txn_date: 'Date',
  bk_sku: 'SKU',
  product_name: 'Product name',
  order_number: 'Order #',
  kind: 'Kind',
  store: 'Store',
  signed_cases: 'Signed cases',
  unit_cost: 'Unit cost',
  linked_return_of_bk_txn_id: 'Linked draw',
  notes: 'Notes',
  username: 'Username',
}

const CANONICAL_OVERRIDE_FIELDS = new Set<string>(OVERRIDE_FIELD_ORDER)

export function getOverrideModalFieldKeys(row: WarehouseLedgerTransactionItem): string[] {
  const snapshot = row.bs_upstream_snapshot ?? {}
  return OVERRIDE_FIELD_ORDER.filter(
    (key) =>
      OVERRIDE_FIELD_LABELS[key] &&
      (CANONICAL_OVERRIDE_FIELDS.has(key) ||
        key in row.override_map ||
        key in snapshot)
  )
}

function formatFieldValue(key: string, value: unknown): string {
  return formatLedgerFieldValue(key, value)
}

function currentValue(row: WarehouseLedgerTransactionItem, key: string): unknown {
  switch (key) {
    case 'txn_date':
      return row.txn_date
    case 'bk_sku':
      return row.bk_sku
    case 'kind':
      return row.kind
    case 'signed_cases':
      return row.signed_cases
    case 'store':
      return row.store
    case 'unit_cost':
      return row.unit_cost
    case 'product_name':
      return row.product_name
    case 'order_number':
      return row.order_number
    case 'username':
      return row.username
    case 'notes':
      return row.notes
    default:
      return null
  }
}

type WarehouseLedgerOverrideModalProps = {
  row: WarehouseLedgerTransactionItem
  onClose: () => void
}

export default function WarehouseLedgerOverrideModal({
  row,
  onClose,
}: WarehouseLedgerOverrideModalProps) {
  const snapshot = row.bs_upstream_snapshot ?? {}
  const fieldKeys = getOverrideModalFieldKeys(row)

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="relative flex max-h-[90vh] w-full max-w-5xl flex-col rounded-xl bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Override details</h3>
            <p className="mt-1 text-xs text-gray-500">
              BK txn {row.bk_txn_id.slice(0, 8)}…
              {row.bs_log_id != null ? ` · BS log ${row.bs_log_id}` : ''}
            </p>
            {row.bs_changed_under_override ? (
              <p className="mt-2 rounded-md bg-amber-50 px-2 py-1 text-xs text-amber-900">
                Bright Sites changed under one or more overridden fields.
              </p>
            ) : null}
          </div>
          <button
            type="button"
            className="rounded p-1 text-gray-500 hover:bg-gray-100"
            aria-label="Close"
            onClick={onClose}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-4 min-h-0 flex-1 overflow-auto">
          <table className="table-forecast border-separate border-spacing-0 table-fixed min-w-full text-sm">
            <thead>
              <tr>
                <th className={`${FIELD_COLUMN_CLASS} text-left`} style={FIELD_HEADER_STYLE}>
                  Field
                </th>
                <th className={`text-left ${VALUE_COLUMN_CLASS}`}>Current value</th>
                <th className={`text-left ${VALUE_COLUMN_CLASS}`}>Bright Sites snapshot</th>
                <th className="text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {fieldKeys.map((key) => {
                const overridden = fieldOverrideDiffersFromSnapshot(row, key)
                const current = currentValue(row, key)
                const upstream = snapshot[key]
                const upstreamDiffers =
                  upstream != null &&
                  formatFieldValue(key, upstream) !== formatFieldValue(key, current)
                return (
                  <tr key={key}>
                    <td
                      className={`${FIELD_COLUMN_CLASS} font-medium text-gray-700`}
                      style={FIELD_CELL_STYLE}
                    >
                      {OVERRIDE_FIELD_LABELS[key]}
                    </td>
                    <td className={`tabular-nums ${VALUE_COLUMN_CLASS}`}>
                      <div className={VALUE_CELL_CONTENT_CLASS}>
                        {formatFieldValue(key, current)}
                      </div>
                    </td>
                    <td className={`tabular-nums text-gray-600 ${VALUE_COLUMN_CLASS}`}>
                      <div className={VALUE_CELL_CONTENT_CLASS}>
                        {upstream != null ? formatFieldValue(key, upstream) : '—'}
                      </div>
                    </td>
                    <td>
                      {overridden ? (
                        <span className="rounded bg-blue-100 px-1.5 py-0.5 text-xs font-medium text-blue-800">
                          Overridden
                        </span>
                      ) : null}
                      {overridden && upstreamDiffers ? (
                        <span className="ml-1 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-900">
                          BS changed
                        </span>
                      ) : null}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-5 flex justify-end">
          <button type="button" className="btn btn-secondary text-sm" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
